import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
    getStatusSummary,
    cancelSearch,
    StatusSummaryResponse,
} from '../API/statusApi';
import type { RootState } from '../store/store';

export type {
    SearchJobStatus,
    OperationalStatus,
    ActiveSearchEntry,
    SystemServiceEntry,
    DataSource,
    StatusSummaryResponse,
} from '../API/statusApi';

interface StatusState {
    summary: StatusSummaryResponse | null;
    loading: boolean;
    error: string | null;
}

const initialState: StatusState = {
    summary: null,
    loading: false,
    error: null,
};

export const fetchStatusSummary = createAsyncThunk(
    'status/fetchStatusSummary',
    async (_, { signal, rejectWithValue }) => {
        try {
            const r = await getStatusSummary(signal);
            return r.data;
        } catch (err: any) {
            return rejectWithValue({ message: err?.message ?? 'Failed to fetch status', retryAfterMs: err?.retryAfterMs ?? null });
        }
    }
);

export const killSearch = createAsyncThunk(
    'status/killSearch',
    async (correlationId: string) => {
        await cancelSearch(correlationId);
        return correlationId;
    }
);

export const statusSlice = createSlice({
    name: 'status',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchStatusSummary.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchStatusSummary.fulfilled, (state, action) => {
                state.loading = false;
                // Preserve KILLING/KILLED status for entries still present in the poll response,
                // so an in-flight or just-completed kill is not overwritten by stale poll data.
                // Self-heal: if the backend reports an authoritative terminal status
                // (DONE, CANCELLED, FAILED), let the backend win — do not preserve local status.
                const preserved = new Map(
                    (state.summary?.activeEntries ?? [])
                        .filter((e) => e.status === 'KILLING' || e.status === 'KILLED')
                        .map((e) => [e.id, e.status])
                );
                state.summary = action.payload;
                if (preserved.size > 0 && state.summary) {
                    for (const entry of state.summary.activeEntries) {
                        const localStatus = preserved.get(entry.id);
                        if (localStatus) {
                            // Only re-pin the local status when the backend returns a transient
                            // status. When the backend reports an authoritative terminal status,
                            // let it win so the UI self-heals rather than pinning forever.
                            const backendIsTransient =
                                entry.status === 'PROCESSING' || entry.status === 'PENDING';
                            if (backendIsTransient) {
                                entry.status = localStatus;
                            }
                        }
                    }
                }
            })
            .addCase(fetchStatusSummary.rejected, (state, action) => {
                state.loading = false;
                state.error = (action.payload as { message?: string } | undefined)?.message || action.error.message || 'Failed to fetch status';
            })
            .addCase(killSearch.pending, (state, action) => {
                if (!state.summary) return;
                const entry = state.summary.activeEntries.find((e) => e.id === action.meta.arg);
                // Only transition to KILLING when the entry is in a killable state.
                // Mirror the UI killable predicate (StatusPage.tsx) — exclude terminal/already-killing
                // statuses so a late-arriving pending action cannot relabel a finished job.
                const NON_KILLABLE = ['DONE', 'CANCELLED', 'KILLED', 'KILLING'] as const;
                if (entry && !(NON_KILLABLE as readonly string[]).includes(entry.status)) {
                    entry.status = 'KILLING';
                }
            })
            .addCase(killSearch.fulfilled, (state, action) => {
                if (!state.summary) return;
                const entry = state.summary.activeEntries.find((e) => e.id === action.payload);
                if (entry) entry.status = 'KILLED';
            })
            .addCase(killSearch.rejected, (state, action) => {
                state.error = action.error.message || 'Failed to kill search';
                const correlationId = action.meta.arg;
                if (state.summary) {
                    const entry = state.summary.activeEntries.find((e) => e.id === correlationId);
                    if (entry && entry.status === 'KILLING') {
                        entry.status = 'PROCESSING';
                    }
                }
            });
    },
});

export const selectStatusSummary = (state: RootState) => state.status.summary;
export const selectStatusLoading = (state: RootState) => state.status.loading;
export const selectStatusError = (state: RootState) => state.status.error;

export default statusSlice.reducer;
